import { auth } from './auth.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export class ApiError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

async function rawFetch(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const finalHeaders = {
    'Accept': 'application/json',
    ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...headers,
  };
  const token = auth.getAccessToken();
  if (token && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: body && !(body instanceof FormData) ? JSON.stringify(body) : body,
      signal,
      credentials: 'include',
    });
  } catch (err) {
    throw new ApiError(err.message || 'Network error', { code: 'network_error' });
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const errBlock = (data && data.error) || {};
    throw new ApiError(errBlock.message || `Request failed (${res.status})`, {
      status: res.status,
      code: errBlock.code,
      details: errBlock.details,
      requestId: errBlock.request_id || res.headers.get('X-Request-ID'),
    });
  }

  return data;
}

let refreshInFlight = null;
async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) return null;
  refreshInFlight = (async () => {
    try {
      const tokens = await rawFetch('/auth/refresh', {
        method: 'POST',
        body: { refresh_token: refreshToken },
      });
      auth.setSession(tokens);
      return tokens.access_token;
    } catch {
      auth.clear();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Authenticated fetch with one retry after a refresh attempt on 401.
 * For unauthenticated calls (login, register, verify) the retry is a no-op since
 * there is no refresh token to use.
 */
export async function api(path, options = {}) {
  try {
    return await rawFetch(path, options);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && auth.getRefreshToken()) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        return await rawFetch(path, options);
      }
    }
    throw err;
  }
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return text; }
}
